/**
 * Subscription Management Page — compact, premium, no-scroll layout.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Star, Crown, ArrowRight, ArrowLeft, Shield, Loader2 } from 'lucide-react';
import { useUser } from '../../../context/UserContext';
import toast from '../../../utils/toast';

const Subscription = () => {
  const navigate = useNavigate();
  const { user, planDisplayName } = useUser();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [currentSubscription, setCurrentSubscription] = useState(null);

  const isDevMock = import.meta.env.DEV && import.meta.env.VITE_DEV_MOCK_USER;

  useEffect(() => {
    if (isDevMock) {
      import('../../../dev/mockBilling.js').then(({ MOCK_PLANS, MOCK_SUBSCRIPTION }) => {
        setPlans(MOCK_PLANS);
        setCurrentSubscription(MOCK_SUBSCRIPTION);
        setBillingCycle(MOCK_SUBSCRIPTION.billing_cycle || 'monthly');
        setLoading(false);
      });
    } else {
      fetchPlans();
      fetchCurrentSubscription();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPlans = async () => {
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/billing/plans`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPlans((data.plans || []).filter(p => p.tier_level < 998));
      }
    } catch { toast.error('Failed to load plans'); }
    finally { setLoading(false); }
  };

  const fetchCurrentSubscription = async () => {
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/stripe/subscription-status`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setCurrentSubscription(data.subscription);
        if (data.subscription) setBillingCycle(data.subscription.billing_cycle || 'monthly');
      }
    } catch { /* silent */ }
  };

  const formatPrice = (plan) => {
    const price = billingCycle === 'yearly' ? plan.price_yearly : plan.price_monthly;
    return billingCycle === 'yearly' ? Math.round(price / 12) : price;
  };

  const getYearlyDiscount = (plan) => {
    if (!plan.price_yearly) return 0;
    return Math.round(((plan.price_monthly - plan.price_yearly / 12) / plan.price_monthly) * 100);
  };

  const handleSelectPlan = (plan) => {
    if (plan.is_decoy) return;
    setSelectedPlan(plan);
  };

  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const handleContinue = async () => {
    if (!selectedPlan) { toast.error('Please select a plan'); return; }

    if (isDevMock) {
      toast.info('Stripe Checkout is not available in mock mode');
      return;
    }

    setCheckoutLoading(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          planId: selectedPlan.plan_id,
          billingCycle,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to create checkout session');
      }

      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      toast.error(err.message || 'Something went wrong. Please try again.');
      setCheckoutLoading(false);
    }
  };

  const currentPlanId = currentSubscription?.plan_id;

  return (
    <div className="overflow-auto flex-1 w-full min-h-0 bg-gray-50">
      <div className="px-4 py-5 mx-auto max-w-6xl sm:px-6">

        {/* Back + header row */}
        <div className="flex justify-between items-center mb-4">
          <button
            onClick={() => navigate('/agent/settings?tab=subscription')}
            className="flex gap-1 items-center text-sm text-gray-500 transition-colors hover:text-gray-700"
            type="button"
          >
            <ArrowLeft className="w-4 h-4" /> Settings
          </button>

          {/* Billing toggle */}
          <div className="inline-flex items-center p-0.5 bg-gray-100 rounded-lg border border-gray-200">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                billingCycle === 'monthly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
              }`}
              type="button"
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${
                billingCycle === 'yearly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
              }`}
              type="button"
            >
              Yearly
              <span className="px-1 py-0.5 text-[9px] font-bold text-green-700 bg-green-100 rounded">SAVE</span>
            </button>
          </div>
        </div>

        {/* Title */}
        <div className="mb-5 text-center">
          <h1 className="mb-1 text-xl font-bold text-gray-900 sm:text-2xl">Choose Your Plan</h1>
          <p className="text-xs text-gray-500">Upgrade or switch plans anytime. All plans include a 14-day free trial.</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
          </div>
        ) : (
          <>
            {/* Plan cards */}
            <div className={`grid gap-3 mt-4 mb-5 ${
              plans.length <= 3 ? 'grid-cols-1 sm:grid-cols-3 max-w-3xl mx-auto'
              : plans.length === 4 ? 'grid-cols-2 lg:grid-cols-4'
              : 'grid-cols-2 lg:grid-cols-5'
            }`}>
              {plans.map((plan) => {
                const isSelected = selectedPlan?.plan_id === plan.plan_id;
                const isCurrent = currentPlanId === plan.plan_id;
                const isBestValue = plan.is_best_value;
                const isRecommended = plan.is_enterprise_recommended && !isBestValue;
                const monthlyPrice = formatPrice(plan);
                const yearlyDiscount = getYearlyDiscount(plan);
                const features = plan.features
                  ? Object.values(plan.features).flat().filter(f => !f.is_boolean_feature || f.feature_value === 'true')
                  : [];

                return (
                  <div
                    key={plan.plan_id}
                    onClick={() => handleSelectPlan(plan)}
                    className={`relative flex flex-col rounded-xl border-2 transition-all cursor-pointer ${
                      isSelected
                        ? 'border-blue-600 shadow-md bg-white'
                        : isBestValue
                        ? 'border-blue-200 bg-blue-50/40 shadow-sm hover:shadow-md hover:border-blue-400'
                        : 'border-gray-200 bg-white shadow-sm hover:shadow-md hover:border-gray-300'
                    }`}
                  >
                    {/* Floating badges */}
                    {isBestValue && (
                      <div className="flex absolute -top-3 right-0 left-0 justify-center">
                        <span className="flex gap-1 items-center px-3 py-1 text-[10px] font-bold tracking-wide text-white bg-blue-600 rounded-full shadow-sm">
                          <Star className="w-3 h-3" /> MOST POPULAR
                        </span>
                      </div>
                    )}
                    {isRecommended && (
                      <div className="flex absolute -top-3 right-0 left-0 justify-center">
                        <span className="flex gap-1 items-center px-3 py-1 text-[10px] font-bold tracking-wide text-white bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full shadow-sm">
                          <Crown className="w-3 h-3" /> RECOMMENDED
                        </span>
                      </div>
                    )}

                    <div className="flex flex-col flex-1 p-4 pt-5">
                      {/* Name + current */}
                      <div className="flex gap-1.5 items-center mb-0.5">
                        <h3 className="text-sm font-bold text-gray-900">{plan.display_name}</h3>
                        {isCurrent && (
                          <span className="px-1.5 py-0.5 text-[9px] font-semibold text-green-700 bg-green-100 rounded-full border border-green-200">
                            Current
                          </span>
                        )}
                      </div>
                      {plan.description && (
                        <p className="mb-3 text-[11px] text-gray-500 leading-snug">{plan.description}</p>
                      )}

                      {/* Price */}
                      <div className="mb-3">
                        <span className="text-2xl font-extrabold text-gray-900">${monthlyPrice}</span>
                        <span className="ml-1 text-xs text-gray-400">/mo</span>
                        {billingCycle === 'yearly' && yearlyDiscount > 0 && (
                          <span className="ml-1.5 text-[10px] font-semibold text-green-600">-{yearlyDiscount}%</span>
                        )}
                      </div>

                      {/* Features */}
                      <ul className="flex-1 mb-3 space-y-1.5">
                        {features.slice(0, 4).map((f, i) => (
                          <li key={i} className="flex gap-1.5 items-start text-xs text-gray-600">
                            <Check className="flex-shrink-0 mt-0.5 w-3.5 h-3.5 text-blue-500" />
                            <span>
                              {f.is_boolean_feature
                                ? f.description
                                : <><strong className="text-gray-900">{f.feature_value}</strong> {f.description}</>}
                            </span>
                          </li>
                        ))}
                      </ul>

                      {/* CTA */}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleSelectPlan(plan); }}
                        className={`w-full py-2 text-xs font-semibold rounded-lg transition-all ${
                          isSelected
                            ? 'bg-blue-600 text-white'
                            : isCurrent
                            ? 'bg-gray-100 text-gray-400 cursor-default'
                            : isBestValue
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-gray-900 text-white hover:bg-gray-800'
                        }`}
                      >
                        {isSelected ? <span className="flex gap-1 justify-center items-center"><Check className="w-3.5 h-3.5" /> Selected</span>
                          : isCurrent ? 'Current Plan'
                          : 'Select'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Continue + trust */}
            <div className="flex flex-col gap-2 items-center">
              <button
                onClick={handleContinue}
                disabled={!selectedPlan || checkoutLoading}
                className="flex gap-2 items-center px-8 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-xl transition-all hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
                type="button"
              >
                {checkoutLoading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting to Checkout...</>
                ) : (
                  <>Continue to Checkout <ArrowRight className="w-4 h-4" /></>
                )}
              </button>
              <p className="flex gap-1 items-center text-[10px] text-gray-400">
                <Shield className="w-3 h-3" /> Secure checkout via Stripe. Cancel anytime.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Subscription;
